/**
 * Number Formatters
 *
 * Locale-aware number formatting using Intl.NumberFormat.
 */

// ═══════════════════════════════════════════════════════════════════════════
// FORMATTERS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Format a number with locale-specific formatting
 *
 * @param value - Number to format
 * @param locale - Locale string (e.g., 'en-US', 'de-DE')
 * @param options - Intl.NumberFormat options
 *
 * @example
 * formatNumber(1234567.89, 'en-US') // "1,234,567.89"
 * formatNumber(1234567.89, 'de-DE') // "1.234.567,89"
 */
export function formatNumber(
  value: number,
  locale: string,
  options: Intl.NumberFormatOptions = {}
): string {
  try {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
      ...options,
    }).format(value);
  } catch {
    return String(value);
  }
}

/**
 * Format a number as percentage
 *
 * @param value - Number to format (0-100 scale)
 * @param locale - Locale string
 *
 * A whole percent in the locale's form — the one precision every surface shows
 * (a MESH pillar read "33.6%" in the drawer and "34%" on Evidence, BW-19).
 *
 * @example
 * formatPercent(75, 'en-US') // "75%"
 * formatPercent(33.6, 'es-ES') // "34 %"
 */
export function formatPercent(value: number | null | undefined, locale: string): string {
  if (value == null || !Number.isFinite(value)) return '—';
  try {
    return new Intl.NumberFormat(locale, {
      style: 'percent',
      maximumFractionDigits: 0,
    }).format(value / 100);
  } catch {
    return `${value}%`;
  }
}

/**
 * Format file size in bytes to human-readable format
 *
 * @param bytes - File size in bytes
 * @param locale - Locale string
 *
 * @example
 * formatFileSize(1024, 'en-US') // "1 KB"
 * formatFileSize(1536000, 'en-US') // "1.5 MB"
 */
export function formatFileSize(bytes: number, locale: string): string {
  if (bytes === 0) return '0 B';

  try {
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const value = bytes / Math.pow(k, i);

    const formatted = new Intl.NumberFormat(locale, {
      maximumFractionDigits: 1,
    }).format(value);

    return `${formatted} ${sizes[i]}`;
  } catch {
    return `${bytes} B`;
  }
}

/**
 * Format number with compact notation
 *
 * @param value - Number to format
 * @param locale - Locale string
 *
 * @example
 * formatCompact(1234, 'en-US') // "1.2K"
 * formatCompact(1500000, 'en-US') // "1.5M"
 */
export function formatCompact(value: number, locale: string): string {
  try {
    return new Intl.NumberFormat(locale, {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value);
  } catch {
    return String(value);
  }
}
